import { styled } from '@mui/material';
import { useId } from 'react';

import { OpenInNewIcon } from '../../../icons';
import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { Button } from '../../atoms/Button';
import { Skeleton } from '../../atoms/Skeleton';
import { type RecordPreviewProps, useRecordPreview } from './useRecordPreview';

type Props = RecordPreviewProps;

type OwnerState = {
  resourceColor: ResourceColorPalette;
};

const Root = styled('section')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${ownerState.resourceColor.main}`,
  backgroundColor: theme.palette.background.paper,
}));

const Heading = styled('h5')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.caption,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  margin: 0,
  fontWeight: theme.typography.fontWeightBold,
  color: ownerState.resourceColor.dark,
}));

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Title = styled('p')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  margin: 0,
  minWidth: 0,
  overflowWrap: 'anywhere',
  color: theme.palette.text.primary,
}));

const Details = styled('ul')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  padding: 0,
  listStyle: 'none',
  color: theme.palette.text.secondary,
  overflowWrap: 'anywhere',
}));

const Note = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(0.5),
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
  '& > svg': {
    flexShrink: 0,
    marginTop: 2,
    fontSize: theme.typography.body1.fontSize,
  },
}));

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(-1),
}));

/** Compact read-only view of an existing record, shown next to something that may duplicate it. */
export function RecordPreview(props: Props) {
  const hook = useRecordPreview(props);
  const headingId = useId();
  const IconComponent = hook.IconComponent;
  const ownerState = { resourceColor: hook.resourceColor };

  return (
    <Root
      aria-labelledby={headingId}
      aria-busy={props.loading || undefined}
      ownerState={ownerState}
      data-testid={props['data-testid'] ?? 'record-preview'}
    >
      <Heading id={headingId} ownerState={ownerState}>
        {IconComponent ? <IconComponent fontSize="inherit" /> : null}
        {props.heading}
      </Heading>

      {props.loading ? (
        <>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="45%" />
        </>
      ) : (
        <>
          <TitleRow>
            {props.title ? <Title>{props.title}</Title> : null}
            {props.tag}
          </TitleRow>
          {hook.details.length > 0 ? (
            <Details>
              {hook.details.map((detail) => (
                <li key={detail}>{detail}</li>
              ))}
            </Details>
          ) : null}
        </>
      )}

      {props.note ? (
        <Note>
          {props.noteIcon}
          <span>{props.note}</span>
        </Note>
      ) : null}

      {props.action && !props.loading ? (
        <Footer>
          <Button
            variant="text"
            size="small"
            color="default"
            href={props.action.href}
            target={props.action.openInNewTab ? '_blank' : undefined}
            rel={props.action.openInNewTab ? 'noopener noreferrer' : undefined}
            endIcon={props.action.openInNewTab ? <OpenInNewIcon /> : undefined}
            onClick={props.action.onClick}
          >
            {props.action.label}
          </Button>
        </Footer>
      ) : null}
    </Root>
  );
}

export type { Props as RecordPreviewProps };
